import { Label } from "@/components/ui/label";
import VideoWorkflowStep from "../videoWorkflow/videoWorkflowStep";
import { GetVideoResponseType } from "../../../../hooks/useGetVideo";
import { useVideoWorkflow } from "../videoWorkflow/videoWorkflowProvider";
import { Separator } from "@/components/ui/separator";
import { SrclangType, getPlatformLanguages } from "@/db/schema";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { LoaderCircle, Trash } from "lucide-react";
import {
    Dubbings,
    Voice,
    voices,
    TTSModel,
    ttsModels,
    defaultTTSModel,
    defaultVoice,
    getVoiceLabel,
} from "@/app/api/[[...route]]/services/ai/ai-video-workflow/types";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import VoicesPlaygroundModal from "@/app/[locale]/(other)/(funnels)/ai/upload-videos/_components/step-2/voicesPlaygroundModal";
import DeleteDubbingsModal from "./deleteDubbingsModal";
import { getLanguageLabel, t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

export default function DubbingsTab({
    videoId,
    captions,
    existingDubbings,
    setReloadVideoKey,
}: {
    videoId: string;
    captions: GetVideoResponseType["data"]["captions"];
    existingDubbings: GetVideoResponseType["data"]["dubbings"];
    setReloadVideoKey: (key: number) => void;
}) {
    const { dir } = useLocale();
    const [dubbings, setDubbings] = useState<Dubbings>({
        model: defaultTTSModel,
        languages: [],
        voice: defaultVoice,
        instructions: "",
    });
    const [loading, setLoading] = useState(false);
    const [text, setText] = useState(""); // for dubbigns playground to share across the 2 instances
    const [languageToDelete, setLanguageToDelete] =
        useState<SrclangType | null>(null);

    const { shouldShowWorkflowStepComponent } = useVideoWorkflow();

    const platformLanguages = getPlatformLanguages();
    const languagesExist = platformLanguages.filter((l) =>
        existingDubbings.some((ds) =>
            ds.languages.some((dsL) => dsL === l.code)
        )
    );
    const languagesLeft = platformLanguages
        .filter(
            (l) =>
                !existingDubbings.some((ds) =>
                    ds.languages.some((dsL) => dsL === l.code)
                )
        )
        .filter(
            (l) => !captions.find((c) => c.srclang === l.code)?.isSpokenLanguage
        );

    const generateMoreDubbings = async () => {
        if (!dubbings.languages.length) {
            toast.error(t("toast.error.no-selected-languages"), {
                keyName: "toast.error.no-selected-languages",
            });
            return;
        }

        setLoading(true);

        const res = await client.api.ai["generate-dubbings"].$post({
            json: {
                videoId,
                dubbings,
            },
        });

        setDubbings((prev) => ({ ...prev, languages: [] }));

        if (!res.ok) {
            toast.error(t("toast.error.default"), {
                keyName: "toast.error.default",
            });
        }

        setLoading(false);
    };

    return (
        <>
            <DeleteDubbingsModal
                videoId={videoId}
                languageToDelete={languageToDelete}
                setLanguageToDelete={setLanguageToDelete}
                setReloadVideoKey={setReloadVideoKey}
            />

            <div className="mt-4">
                <div className="mb-6">
                    <Label className="">
                        {t("video-sheet.dubbings-tab.title", undefined, undefined)}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        {t(
                            "video-sheet.dubbings-tab.description",
                            undefined,
                            undefined
                        )}
                    </p>
                </div>

                {shouldShowWorkflowStepComponent("dubbings") ? (
                    <VideoWorkflowStep
                        stepName="dubbings"
                        texts={{
                            title: {
                                keyName:
                                    "video-sheet.create-dubbings-with-ai.title",
                                defaultValue: "יצירת דיבובים עם AI",
                            },
                            description: {
                                keyName:
                                    "video-sheet.create-dubbings-with-ai.description",
                                defaultValue:
                                    "בחרו את השפות אליהן תרצו לדבב את השמע בסרטון",
                            },
                            generateBtn: {
                                keyName:
                                    "video-sheet.generate-dubbings-with-ai-btn",
                                defaultValue: "דיבוב",
                            },
                        }}
                    />
                ) : (
                    <div className="bg-card rounded-md border p-4 flex flex-col w-full">
                        <div className="flex flex-row">
                            <p className="shrink-0 w-[150px] sm:w-[180px] text-sm font-medium">
                                {t(
                                    "video-sheet.dubbings-tab.existing.label",
                                    undefined,
                                    undefined
                                )}
                            </p>
                            <p className="text-sm">
                                {languagesExist.map((c, i) => (
                                    <span
                                        className="inline-flex items-center"
                                        key={c.code}
                                    >
                                        <button
                                            className="me-0.5"
                                            onClick={() =>
                                                setLanguageToDelete(c.code)
                                            }
                                        >
                                            <Trash className="size-3.5" />
                                        </button>{" "}
                                        {c.label}
                                        <span className="me-1.5">
                                            {i < languagesExist.length - 1 &&
                                                ", "}
                                            {i === languagesExist.length - 1 &&
                                                "."}
                                        </span>
                                    </span>
                                ))}
                            </p>
                        </div>

                        <Separator className="my-4" />
                        <div className="flex flex-row items-center">
                            <p className="shrink-0 w-[150px] sm:w-[180px] text-sm font-medium">
                                {t(
                                    "video-sheet.dubbings-tab.create-more.label",
                                    undefined,
                                    undefined
                                )}
                            </p>
                            {languagesLeft.length > 0 ? (
                                <div className="flex flex-col w-full gap-2">
                                    <p className="-ms-1">
                                        {languagesLeft.map((language) => {
                                            const selected =
                                                !!dubbings.languages?.find(
                                                    (srclang) =>
                                                        srclang ===
                                                        language.code
                                                );
                                            const hasTranslation =
                                                captions.some(
                                                    (c) =>
                                                        c.srclang ===
                                                        language.code
                                                );

                                            return (
                                                <button
                                                    className={cn(
                                                        "m-0.5 inline-flex items-center transition rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset",
                                                        selected &&
                                                            "bg-blue-50 hover:bg-blue-100 text-blue-700 ring-blue-500",
                                                        !selected &&
                                                            "bg-gray-50 hover:bg-gray-100 text-gray-600 ring-gray-500/10"
                                                    )}
                                                    onClick={() => {
                                                        if (selected) {
                                                            setDubbings(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    languages:
                                                                        prev.languages.filter(
                                                                            (
                                                                                srclang
                                                                            ) =>
                                                                                srclang !==
                                                                                language.code
                                                                        ),
                                                                })
                                                            );
                                                        } else {
                                                            if (
                                                                !hasTranslation
                                                            ) {
                                                                toast.error(
                                                                    t(
                                                                        "toast.error.dubbings.translation-required"
                                                                    ),
                                                                    {
                                                                        keyName:
                                                                            "toast.error.dubbings.translation-required",
                                                                    }
                                                                );
                                                                return;
                                                            }
                                                            setDubbings(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    languages: [
                                                                        ...prev.languages,
                                                                        language.code,
                                                                    ],
                                                                })
                                                            );
                                                        }
                                                    }}
                                                    key={language.code}
                                                >
                                                    {getLanguageLabel(language.code)}
                                                </button>
                                            );
                                        })}
                                    </p>

                                    <Select
                                        value={dubbings.model}
                                        onValueChange={(val) =>
                                            setDubbings({
                                                ...dubbings,
                                                model: val as TTSModel,
                                            })
                                        }
                                        dir={dir}
                                    >
                                        <div className="flex">
                                            <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                                {t(
                                                    "video-sheet.dubbings-tab.model.label",
                                                    undefined,
                                                    undefined
                                                )}
                                            </div>

                                            <SelectTrigger className="rounded-s-none border-s-0 h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                        </div>

                                        <SelectContent>
                                            {ttsModels.map((model, idx) => (
                                                <SelectItem
                                                    key={idx}
                                                    value={model}
                                                >
                                                    {model}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select
                                        value={dubbings.voice}
                                        onValueChange={(val) =>
                                            setDubbings({
                                                ...dubbings,
                                                voice: val as Voice,
                                            })
                                        }
                                        dir={dir}
                                    >
                                        <div className="flex">
                                            <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                                {t(
                                                    "video-sheet.dubbings-tab.voice.label",
                                                    undefined,
                                                    undefined
                                                )}
                                            </div>

                                            <SelectTrigger className="rounded-s-none border-s-0 h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                        </div>

                                        <SelectContent>
                                            {voices.map((voice, idx) => (
                                                <SelectItem
                                                    key={idx}
                                                    value={voice}
                                                >
                                                    {getVoiceLabel(voice)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div className="flex">
                                        <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                            {t(
                                                "video-sheet.dubbings-tab.instructions.label",
                                                undefined,
                                                undefined
                                            )}
                                        </div>
                                        <Input
                                            value={dubbings.instructions}
                                            onChange={(e) =>
                                                setDubbings((prev) => ({
                                                    ...prev,
                                                    instructions:
                                                        e.target.value,
                                                }))
                                            }
                                            className="rounded-s-none border-s-0"
                                        />
                                    </div>
                                    <div className="flex w-full">
                                        <VoicesPlaygroundModal
                                            text={text}
                                            setText={setText}
                                            dubbings={dubbings}
                                            setDubbings={(
                                                dubbingsArg: Dubbings
                                            ) => setDubbings(dubbingsArg)}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm">
                                    {t("common.no-more-options")}
                                </p>
                            )}
                        </div>

                        {languagesLeft.length > 0 && (
                            <Button
                                onClick={generateMoreDubbings}
                                disabled={
                                    dubbings.languages.length === 0 || loading
                                }
                                className="mt-4 w-fit"
                            >
                                {loading ? (
                                    <LoaderCircle className="size-4 animate-spin" />
                                ) : (
                                    t("common.create")
                                )}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
